import { createFileRoute } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  Search,
  IndianRupee,
  CheckCircle2,
  Clock,
  XCircle,
  RefreshCw,
  Check,
  X,
  Banknote,
} from "lucide-react";
import { listPayments, type PaymentStatus } from "@/lib/payments-api";
import { listBookings, type Booking } from "@/lib/bookings-api";
import {
  listRefundRequests,
  decideRefundRequest,
  processPaymentRefund,
  type RefundRequest,
  type RefundStatus,
} from "@/lib/refunds-api";

export const Route = createFileRoute("/admin/payments")({ component: PaymentsAdmin });

const STATUS_STYLE: Record<PaymentStatus, string> = {
  success: "bg-green-100 text-green-700",
  pending: "bg-primary/12 text-primary",
  failed: "bg-destructive/10 text-destructive",
  refunded: "bg-muted text-muted-foreground",
};

const REFUND_STATUS_STYLE: Record<RefundStatus, string> = {
  approved: "bg-green-100 text-green-700",
  pending: "bg-primary/12 text-primary",
  rejected: "bg-destructive/10 text-destructive",
  processed: "bg-blue-100 text-blue-700",
};

function inr(n: number) {
  return `₹${n.toLocaleString("en-IN")}`;
}

function refundAmountOf(r: RefundRequest, booking?: Booking): number {
  return r.amount || booking?.refund_amount || r.booking_amount || 0;
}

/**
 * Prefer refund-request `status` from the API (backend sets `processed` after cancel).
 * Fallback: approved + booking already cancelled (legacy rows before backend sync).
 */
function refundDisplayStatus(r: RefundRequest, booking?: Booking): RefundStatus {
  if (r.status === "processed") {
    return "processed";
  }
  if (r.status !== "approved") {
    return r.status;
  }
  const bookingCancelled =
    Boolean(booking?.cancelled_at) ||
    booking?.status === "cancelled" ||
    r.booking_status === "cancelled";
  return bookingCancelled ? "processed" : "approved";
}

function PaymentsAdmin() {
  const [view, setView] = useState<"payments" | "refunds">("payments");

  return (
    <div className="min-w-0 space-y-5">
      <div className="inline-flex rounded-full border border-border bg-card p-1 text-sm">
        {(
          [
            { key: "payments", label: "Payments" },
            { key: "refunds", label: "Refund Requests" },
          ] as const
        ).map((v) => (
          <button
            key={v.key}
            onClick={() => setView(v.key)}
            className={`rounded-full px-4 py-1.5 font-semibold transition-colors ${
              view === v.key
                ? "bg-primary text-primary-foreground"
                : "text-muted-foreground hover:text-foreground"
            }`}
          >
            {v.label}
          </button>
        ))}
      </div>

      {view === "payments" ? <PaymentsList /> : <RefundsList />}
    </div>
  );
}

function PaymentsList() {
  const [q, setQ] = useState("");
  const [tab, setTab] = useState<"all" | PaymentStatus>("all");

  const {
    data: list = [],
    isLoading,
    isFetching,
    error,
    refetch,
  } = useQuery({ queryKey: ["admin-payments"], queryFn: listPayments });

  const counts = {
    all: list.length,
    success: list.filter((p) => p.status === "success").length,
    pending: list.filter((p) => p.status === "pending").length,
    failed: list.filter((p) => p.status === "failed").length,
    refunded: list.filter((p) => p.status === "refunded").length,
  };

  const totals = useMemo(
    () => ({
      collected: list.filter((p) => p.status === "success").reduce((s, p) => s + p.amount, 0),
      pending: list.filter((p) => p.status === "pending").reduce((s, p) => s + p.amount, 0),
      refunded: list.filter((p) => p.status === "refunded").reduce((s, p) => s + p.amount, 0),
    }),
    [list],
  );

  const filtered = list
    .filter((p) => (tab === "all" ? true : p.status === tab))
    .filter((p) =>
      (p.order_id + p.payer_name + p.phone + p.email + p.method)
        .toLowerCase()
        .includes(q.toLowerCase()),
    );

  const STATS = [
    { label: "Collected", value: inr(totals.collected), icon: CheckCircle2 },
    { label: "Pending", value: inr(totals.pending), icon: Clock },
    { label: "Refunded", value: inr(totals.refunded), icon: XCircle },
    { label: "Transactions", value: String(counts.all), icon: IndianRupee },
  ];

  return (
    <div className="min-w-0 space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="font-serif text-2xl font-bold">Payments</h2>
          <p className="text-sm text-muted-foreground">All yatra payments and their status</p>
        </div>
        <div className="flex items-center gap-2">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search..."
              className="rounded-full border border-border bg-card py-2 pl-9 pr-4 text-sm outline-none focus:border-primary"
            />
          </div>
          <button
            onClick={() => refetch()}
            disabled={isFetching}
            className="rounded-full border border-border bg-card p-2 text-muted-foreground transition-colors hover:text-foreground disabled:opacity-50"
            title="Refresh"
          >
            <RefreshCw className={`h-4 w-4 ${isFetching ? "animate-spin" : ""}`} />
          </button>
        </div>
      </div>

      {error && (
        <div className="rounded-2xl border border-destructive/30 bg-destructive/10 p-3 text-sm text-destructive">
          {(error as Error).message}
        </div>
      )}

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        {STATS.map((s) => (
          <div key={s.label} className="rounded-2xl border border-border bg-card p-4">
            <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-muted-foreground">
              <s.icon className="h-4 w-4" /> {s.label}
            </div>
            <div className="mt-1 text-2xl font-bold tabular-nums">{s.value}</div>
          </div>
        ))}
      </div>

      {/* Status tabs */}
      <div className="inline-flex flex-wrap rounded-full border border-border bg-card p-1 text-sm">
        {(
          [
            { key: "all", label: "All", count: counts.all },
            { key: "success", label: "Success", count: counts.success },
            { key: "pending", label: "Pending", count: counts.pending },
            { key: "failed", label: "Failed", count: counts.failed },
            { key: "refunded", label: "Refunded", count: counts.refunded },
          ] as const
        ).map((tp) => (
          <button
            key={tp.key}
            onClick={() => setTab(tp.key)}
            className={`rounded-full px-4 py-1.5 font-semibold transition-colors ${
              tab === tp.key
                ? "bg-primary text-primary-foreground"
                : "text-muted-foreground hover:text-foreground"
            }`}
          >
            {tp.label}
            <span
              className={`ml-1.5 rounded-full px-1.5 text-[10px] ${
                tab === tp.key ? "bg-primary-foreground/20" : "bg-muted"
              }`}
            >
              {tp.count}
            </span>
          </button>
        ))}
      </div>

      <div className="-mx-4 overflow-hidden rounded-2xl border border-border bg-card sm:mx-0">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[820px] text-sm">
            <thead className="bg-muted text-left text-xs uppercase tracking-wider text-muted-foreground">
              <tr>
                <th className="p-3">Order ID</th>
                <th className="p-3">Payer</th>
                <th className="p-3">Contact</th>
                <th className="p-3">Amount</th>
                <th className="p-3">Method</th>
                <th className="p-3">Status</th>
                <th className="p-3">Date</th>
              </tr>
            </thead>
            <tbody>
              {isLoading && (
                <tr>
                  <td colSpan={7} className="p-8 text-center text-sm text-muted-foreground">
                    Loading payments…
                  </td>
                </tr>
              )}
              {!isLoading && filtered.length === 0 && (
                <tr>
                  <td colSpan={7} className="p-8 text-center text-sm text-muted-foreground">
                    No payments yet.
                  </td>
                </tr>
              )}
              {filtered.map((p) => (
                <tr key={p.id} className="border-t border-border">
                  <td className="p-3 font-mono text-xs">{p.order_id || p.payment_id || p.id}</td>
                  <td className="p-3 font-semibold">{p.payer_name || "—"}</td>
                  <td className="p-3 text-muted-foreground">
                    <div>{p.phone}</div>
                    <div className="text-xs">{p.email}</div>
                  </td>
                  <td className="p-3 font-semibold">{inr(p.amount)}</td>
                  <td className="p-3 capitalize text-muted-foreground">{p.method}</td>
                  <td className="p-3">
                    <span
                      className={`rounded-full px-2 py-0.5 text-xs font-semibold capitalize ${STATUS_STYLE[p.status]}`}
                    >
                      {p.status}
                    </span>
                  </td>
                  <td className="p-3 text-muted-foreground">
                    {new Date(p.created_at).toLocaleDateString("en-IN")}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function RefundsList() {
  const qc = useQueryClient();
  const [q, setQ] = useState("");
  const [tab, setTab] = useState<"all" | RefundStatus>("all");
  const [decision, setDecision] = useState<{ row: RefundRequest; status: RefundStatus } | null>(
    null,
  );
  const [processTarget, setProcessTarget] = useState<RefundRequest | null>(null);
  const [note, setNote] = useState("");

  const {
    data: list = [],
    isLoading,
    isFetching,
    error,
    refetch,
  } = useQuery({
    queryKey: ["admin-refund-requests"],
    queryFn: () => listRefundRequests(),
  });

  const { data: bookings = [] } = useQuery({
    queryKey: ["jgy-bookings"],
    queryFn: listBookings,
  });

  const bookingById = useMemo(() => new Map(bookings.map((b) => [b.id, b])), [bookings]);

  const decide = useMutation({
    mutationFn: decideRefundRequest,
    onSuccess: (result, vars) => {
      toast.success(vars.status === "approved" ? "Refund approved" : `Refund ${vars.status}`);
      setDecision(null);
      setNote("");
      // Keep the status the admin chose — API may return a different value.
      qc.setQueryData<RefundRequest[]>(["admin-refund-requests"], (old) =>
        (old ?? []).map((r) =>
          r.id === result.id ? { ...result, status: vars.status, admin_note: vars.admin_note } : r,
        ),
      );
      qc.invalidateQueries({ queryKey: ["admin-refund-requests"] });
      qc.invalidateQueries({ queryKey: ["admin-payments"] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const processRefund = useMutation({
    mutationFn: processPaymentRefund,
    onSuccess: (result) => {
      toast.success(
        result.refund_message ||
          (result.admin_note === "Booking already cancelled"
            ? "Refund already completed for this booking"
            : "Refund completed successfully"),
      );
      setProcessTarget(null);
      qc.setQueryData<RefundRequest[]>(["admin-refund-requests"], (old) =>
        (old ?? []).map((r) => (r.id === result.id ? result : r)),
      );
      qc.invalidateQueries({ queryKey: ["admin-refund-requests"] });
      qc.invalidateQueries({ queryKey: ["admin-payments"] });
      qc.invalidateQueries({ queryKey: ["jgy-bookings"] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const displayStatus = (r: RefundRequest) => refundDisplayStatus(r, bookingById.get(r.booking_id));

  const counts = {
    all: list.length,
    pending: list.filter((r) => displayStatus(r) === "pending").length,
    approved: list.filter((r) => displayStatus(r) === "approved").length,
    rejected: list.filter((r) => displayStatus(r) === "rejected").length,
    processed: list.filter((r) => displayStatus(r) === "processed").length,
  };

  const requestedTotal = useMemo(
    () =>
      list
        .filter((r) => r.status === "pending")
        .reduce((s, r) => s + (r.booking_amount || r.amount), 0),
    [list],
  );

  const filtered = list
    .filter((r) => (tab === "all" ? true : displayStatus(r) === tab))
    .filter((r) =>
      (
        r.requester_name +
        r.phone +
        r.email +
        r.order_id +
        r.payment_id +
        r.booking_id +
        r.reason +
        r.bank_name +
        r.account_holder_name
      )
        .toLowerCase()
        .includes(q.toLowerCase()),
    );

  return (
    <div className="min-w-0 space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="font-serif text-2xl font-bold">Refund Requests</h2>
          <p className="text-sm text-muted-foreground">
            {counts.pending} awaiting review · {counts.approved} approved · {counts.processed}{" "}
            processed · {inr(requestedTotal)} requested
          </p>
        </div>
        <div className="flex items-center gap-2">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search..."
              className="rounded-full border border-border bg-card py-2 pl-9 pr-4 text-sm outline-none focus:border-primary"
            />
          </div>
          <button
            onClick={() => refetch()}
            disabled={isFetching}
            className="rounded-full border border-border bg-card p-2 text-muted-foreground transition-colors hover:text-foreground disabled:opacity-50"
            title="Refresh"
          >
            <RefreshCw className={`h-4 w-4 ${isFetching ? "animate-spin" : ""}`} />
          </button>
        </div>
      </div>

      {error && (
        <div className="rounded-2xl border border-destructive/30 bg-destructive/10 p-3 text-sm text-destructive">
          {(error as Error).message}
        </div>
      )}

      <div className="inline-flex flex-wrap rounded-full border border-border bg-card p-1 text-sm">
        {(
          [
            { key: "all", label: "All", count: counts.all },
            { key: "pending", label: "Pending", count: counts.pending },
            { key: "approved", label: "Approved", count: counts.approved },
            { key: "rejected", label: "Rejected", count: counts.rejected },
            { key: "processed", label: "Processed", count: counts.processed },
          ] as const
        ).map((tp) => (
          <button
            key={tp.key}
            onClick={() => setTab(tp.key)}
            className={`rounded-full px-4 py-1.5 font-semibold transition-colors ${
              tab === tp.key
                ? "bg-primary text-primary-foreground"
                : "text-muted-foreground hover:text-foreground"
            }`}
          >
            {tp.label}
            <span
              className={`ml-1.5 rounded-full px-1.5 text-[10px] ${
                tab === tp.key ? "bg-primary-foreground/20" : "bg-muted"
              }`}
            >
              {tp.count}
            </span>
          </button>
        ))}
      </div>

      <div className="-mx-4 overflow-hidden rounded-2xl border border-border bg-card sm:mx-0">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[1080px] text-sm">
            <thead className="bg-muted text-left text-xs uppercase tracking-wider text-muted-foreground">
              <tr>
                <th className="p-3">Requester</th>
                <th className="p-3">Contact</th>
                <th className="p-3">Booking / Payment</th>
                <th className="p-3">Booking amount</th>
                <th className="p-3">Bank details</th>
                <th className="p-3">Reason</th>
                <th className="p-3">Status</th>
                <th className="p-3">Requested</th>
                <th className="p-3">Action</th>
              </tr>
            </thead>
            <tbody>
              {isLoading && (
                <tr>
                  <td colSpan={9} className="p-8 text-center text-sm text-muted-foreground">
                    Loading refund requests…
                  </td>
                </tr>
              )}
              {!isLoading && filtered.length === 0 && (
                <tr>
                  <td colSpan={9} className="p-8 text-center text-sm text-muted-foreground">
                    No refund requests here.
                  </td>
                </tr>
              )}
              {filtered.map((r) => {
                const booking = bookingById.get(r.booking_id);
                const status = displayStatus(r);
                const refunded = refundAmountOf(r, booking);
                return (
                  <tr key={r.id} className="border-t border-border align-top">
                    <td className="p-3 font-semibold">{r.requester_name || "—"}</td>
                    <td className="p-3 text-muted-foreground">
                      <div>{r.phone}</div>
                      <div className="text-xs">{r.email}</div>
                    </td>
                    <td className="p-3 font-mono text-xs text-muted-foreground">
                      <div>{r.booking_id || "—"}</div>
                      <div>{r.payment_id || r.order_id}</div>
                    </td>
                    <td className="p-3 font-semibold">
                      {inr(r.booking_amount || r.amount)}
                      {status === "processed" && refunded > 0 && (
                        <div className="text-xs font-normal text-green-700">
                          Refunded {inr(refunded)}
                        </div>
                      )}
                      {status !== "processed" &&
                        r.amount > 0 &&
                        r.booking_amount > 0 &&
                        r.amount !== r.booking_amount && (
                          <div className="text-xs font-normal text-muted-foreground">
                            Refund: {inr(r.amount)}
                          </div>
                        )}
                    </td>
                    <td className="p-3 text-xs text-muted-foreground">
                      {r.bank_name ? (
                        <>
                          <div className="font-medium text-foreground">{r.account_holder_name}</div>
                          <div>{r.bank_name}</div>
                          <div className="font-mono">
                            {r.ifsc_code}
                            {r.account_number ? ` · ••••${r.account_number.slice(-4)}` : ""}
                          </div>
                        </>
                      ) : (
                        "—"
                      )}
                    </td>
                    <td className="p-3 max-w-[220px] text-muted-foreground">
                      <div>{r.reason || "—"}</div>
                      {r.admin_note && (
                        <div className="mt-1 text-xs italic">Note: {r.admin_note}</div>
                      )}
                    </td>
                    <td className="p-3">
                      <span
                        className={`rounded-full px-2 py-0.5 text-xs font-semibold capitalize ${REFUND_STATUS_STYLE[status]}`}
                      >
                        {status}
                      </span>
                      {status === "processed" && (
                        <div className="mt-1 space-y-0.5 text-[10px] text-muted-foreground">
                          {r.refund_method && (
                            <div className="capitalize">via {r.refund_method}</div>
                          )}
                          {booking?.cancelled_at && (
                            <div>
                              Cancelled {new Date(booking.cancelled_at).toLocaleDateString("en-IN")}
                            </div>
                          )}
                          {r.reviewed_at && (
                            <div>
                              Processed {new Date(r.reviewed_at).toLocaleDateString("en-IN")}
                            </div>
                          )}
                          {r.refund_message && (
                            <div className="max-w-[180px] leading-snug" title={r.refund_message}>
                              {r.refund_message}
                            </div>
                          )}
                        </div>
                      )}
                      {status === "approved" && r.reviewed_at && (
                        <div className="mt-1 text-[10px] text-muted-foreground">
                          Approved {new Date(r.reviewed_at).toLocaleDateString("en-IN")}
                        </div>
                      )}
                      {status === "rejected" && r.reviewed_at && (
                        <div className="mt-1 text-[10px] text-muted-foreground">
                          Rejected {new Date(r.reviewed_at).toLocaleDateString("en-IN")}
                        </div>
                      )}
                    </td>
                    <td className="p-3 text-muted-foreground">
                      {r.created_at ? new Date(r.created_at).toLocaleDateString("en-IN") : "—"}
                    </td>
                    <td className="p-3">
                      {status === "pending" && (
                        <div className="flex gap-1">
                          <button
                            onClick={() => {
                              setDecision({ row: r, status: "approved" });
                              setNote(r.admin_note);
                            }}
                            className="rounded-full bg-green-100 p-1.5 text-green-700 hover:bg-green-200"
                            title="Approve"
                          >
                            <Check className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => {
                              setDecision({ row: r, status: "rejected" });
                              setNote(r.admin_note);
                            }}
                            className="rounded-full bg-destructive/10 p-1.5 text-destructive hover:bg-destructive/20"
                            title="Reject"
                          >
                            <X className="h-4 w-4" />
                          </button>
                        </div>
                      )}
                      {status === "approved" && (
                        <button
                          onClick={() => setProcessTarget(r)}
                          className="btn-primary inline-flex items-center gap-1 py-1.5 text-xs"
                          title="Initiate payment refund"
                        >
                          <Banknote className="h-3.5 w-3.5" />
                          Refund
                        </button>
                      )}
                      {status === "processed" && (
                        <div className="text-xs text-green-700">
                          <div className="font-semibold">Refunded</div>
                          {r.refund_method && (
                            <div className="capitalize text-muted-foreground">
                              {r.refund_method}
                            </div>
                          )}
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {processTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="w-full max-w-md rounded-2xl border border-border bg-card p-5">
            <h3 className="font-serif text-lg font-bold">Initiate payment refund</h3>
            <p className="mt-1 text-sm text-muted-foreground">
              {processTarget.requester_name || "Request"} ·{" "}
              {inr(processTarget.booking_amount || processTarget.amount)}
            </p>
            <div className="mt-4 space-y-2 rounded-xl border border-border bg-muted/40 p-3 text-xs text-muted-foreground">
              <div>
                Booking #{processTarget.booking_id || "—"} · Payment{" "}
                {processTarget.payment_id || processTarget.order_id || "—"}
              </div>
              {processTarget.bank_name ? (
                <>
                  <div className="font-medium text-foreground">
                    {processTarget.account_holder_name} · {processTarget.bank_name}
                  </div>
                  <div className="font-mono">
                    {processTarget.ifsc_code} · {processTarget.account_number}
                  </div>
                  <div className="italic">On file for admin reference only.</div>
                </>
              ) : (
                <div>No bank details on this request.</div>
              )}
            </div>
            <p className="mt-3 text-xs text-muted-foreground">
              Calls{" "}
              <span className="font-mono">
                POST /visit-bookings/{processTarget.booking_id}/cancel/
              </span>{" "}
              (no body). Refund goes via Razorpay to the original payment method; the backend marks
              this refund request as processed.
            </p>
            <div className="mt-4 flex justify-end gap-2">
              <button
                onClick={() => setProcessTarget(null)}
                className="rounded-full border border-border px-4 py-2 text-sm font-semibold"
              >
                Cancel
              </button>
              <button
                disabled={processRefund.isPending}
                onClick={() => processRefund.mutate(processTarget)}
                className="btn-primary py-2 text-sm disabled:opacity-50"
              >
                {processRefund.isPending ? "Processing…" : "Confirm refund"}
              </button>
            </div>
          </div>
        </div>
      )}

      {decision && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="w-full max-w-md rounded-2xl border border-border bg-card p-5">
            <h3 className="font-serif text-lg font-bold">
              {decision.status === "approved" ? "Approve" : "Reject"} refund
            </h3>
            <p className="mt-1 text-sm text-muted-foreground">
              {decision.row.requester_name || "Request"} ·{" "}
              {inr(decision.row.booking_amount || decision.row.amount)}
            </p>
            <label className="mt-4 block text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              Admin note
            </label>
            <textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              rows={3}
              placeholder="e.g. verified"
              className="mt-1 w-full rounded-xl border border-border bg-background p-3 text-sm outline-none focus:border-primary"
            />
            <div className="mt-4 flex justify-end gap-2">
              <button
                onClick={() => setDecision(null)}
                className="rounded-full border border-border px-4 py-2 text-sm font-semibold"
              >
                Cancel
              </button>
              <button
                disabled={decide.isPending}
                onClick={() =>
                  decide.mutate({
                    id: decision.row.id,
                    status: decision.status,
                    admin_note: note,
                  })
                }
                className="btn-primary py-2 text-sm disabled:opacity-50"
              >
                {decide.isPending ? "Saving…" : "Confirm"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
